/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';

import { ColDef } from 'ag-grid-community';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Input } from 'src/components/Input';
import { thirdPartyDeductionCapsInputValidationRules } from 'src/components/RateCardDetail/ContractTerms/contractTermValidation';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { IContractTermsComponentProps } from 'src/types/IRateCard';
import { hasErrorMessage } from 'src/util/validationsUtil';

import { DEFAULT_EMPTY_TERRITORY, TerritoryBasedGrid, TerritoryBasedGridConfig } from '../TerritoryBasedGrid';
import './ThirdPartyDeductionCaps.scss';

export const ThirdPartyDeductionCaps = ({
    contractTerms,
    handleContractTermValueChange,
    setIsContractTermInputValid,
    isEditMode,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages
}: IContractTermsComponentProps) => {
    const handleSingleErrorMsgChange = (field: string, value: string) => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange('thirdPartyDeductionCaps', field, value);
    };

    useEffect(() => {
        if (!setIsContractTermInputValid) return;
        setIsContractTermInputValid((prevState) => ({
            ...prevState,
            thirdPartyDeductionCaps: !hasErrorMessage(contractTermErrorMessages!.thirdPartyDeductionCaps)
        }));
    }, [contractTermErrorMessages!.thirdPartyDeductionCaps]);

    // Column definitions for the territory overrides grid
    const colDefs: ColDef[] = [
        {
            headerName: '',
            field: 'checkBoxBtn',
            floatingFilter: false,
            hide: !isEditMode,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            width: 50,
            pinned: 'left',
            sortable: false
        },
        {
            headerName: 'Territory',
            field: 'territory',
            floatingFilter: false,
            editable: isEditMode,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            sortable: false
        },
        {
            headerName: 'Override deduction cap',
            field: 'deductionCap',
            floatingFilter: false,
            editable: isEditMode,
            sortable: false
        }
    ];

    // Grid configuration for territory overrides
    const gridConfig: TerritoryBasedGridConfig = {
        contractTermKey: 'thirdPartyDeductionCaps',
        dataPath: 'territoryOverrides',
        columns: colDefs,
        createNewRow: () => ({ territory: DEFAULT_EMPTY_TERRITORY, deductionCap: 0 }),
        subheading: 'Territory overrides for PSM deduction caps',
        showPagination: true,
        paginationPageSize: 30,
        gridClassName: 'territory-overrides-grid'
    };

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="none">
                    <Heading level={3}>Third party deduction caps</Heading>
                    <ContractTermInfoButton contractTermName={ContractTermName.ThirdPartyDeductionCaps} />
                </Row>

                <Heading level={5}>PSM deduction caps</Heading>
                <Input
                    label="3rd party billing"
                    value={(contractTerms.thirdPartyDeductionCaps?.globalDeductionCap || '').toString()}
                    isRequired={true}
                    isEditMode={isEditMode}
                    type="number"
                    onChange={(newVal) =>
                        handleContractTermValueChange('thirdPartyDeductionCaps', 'globalDeductionCap', newVal)
                    }
                    errorMessage={contractTermErrorMessages!.thirdPartyDeductionCaps.globalDeductionCap}
                    handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('globalDeductionCap', newVal)}
                    validationRules={thirdPartyDeductionCapsInputValidationRules}
                />
                <TerritoryBasedGrid
                    config={gridConfig}
                    contractTerms={contractTerms}
                    isEditMode={isEditMode}
                    handleContractTermValueChange={handleContractTermValueChange}
                    handleContractTermErrorMessageChange={handleContractTermErrorMessageChange}
                    contractTermErrorMessages={contractTermErrorMessages}
                />

                <Heading level={5}>Revenue deduction caps</Heading>
                <Row>
                    <Input
                        label="App store"
                        value={(contractTerms.thirdPartyDeductionCaps?.appStore || '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        type="number"
                        onChange={(newVal) =>
                            handleContractTermValueChange('thirdPartyDeductionCaps', 'appStore', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.thirdPartyDeductionCaps.appStore}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('appStore', newVal)}
                        validationRules={thirdPartyDeductionCapsInputValidationRules}
                    />
                    <Input
                        label="Carrier billing"
                        value={(contractTerms.thirdPartyDeductionCaps?.carrierBilling || '').toString()}
                        isRequired={true}
                        isEditMode={isEditMode}
                        type="number"
                        onChange={(newVal) =>
                            handleContractTermValueChange('thirdPartyDeductionCaps', 'carrierBilling', newVal)
                        }
                        errorMessage={contractTermErrorMessages!.thirdPartyDeductionCaps.carrierBilling}
                        handleErrMsgChange={(newVal) => handleSingleErrorMsgChange('carrierBilling', newVal)}
                        validationRules={thirdPartyDeductionCapsInputValidationRules}
                    />
                </Row>
                <Row>
                    <Toggle
                        label="App store deduction cap is based on retail price"
                        value={contractTerms.thirdPartyDeductionCaps!.appStoreUsesRetailPrice!}
                        isEditMode={isEditMode}
                        onChange={(newVal) =>
                            handleContractTermValueChange('thirdPartyDeductionCaps', 'appStoreUsesRetailPrice', newVal)
                        }
                    />
                </Row>
            </Column>
        </Box>
    );
};
